import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle, HelpCircle } from "lucide-react";
import axios from "axios";

const ADMIN_EMAIL = "sellarsscott@gmail.com";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("If an account exists, a reset link has been sent.");
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
      toast.success("If an account exists, a reset link has been sent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left side - Branding */}
      <div className="login-image">
        <img
          src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200"
          alt="Golf links"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-12">
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
            alt="Royal Dornoch Golf Club"
            className="w-40 h-40 object-contain mb-8 bg-white rounded-full p-2"
          />
          <h1 className="text-4xl text-white text-center mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
            Staff Training Portal
          </h1>
          <p className="text-white/80 text-center text-lg max-w-md">
            Delivering excellence through knowledge and service
          </p>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-white/40"></div>
            <span className="text-white/60 text-sm tracking-widest uppercase">Est. 1616</span>
            <div className="h-px w-12 bg-white/40"></div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8 lg:p-12 bg-[var(--rd-cream)]">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
              alt="Royal Dornoch Golf Club"
              className="w-24 h-24 object-contain mb-4"
            />
            <h1 className="text-xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
              Staff Training
            </h1>
          </div>

          {/* Back to login link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-[var(--rd-green)] hover:underline mb-8"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>

          {!submitted ? (
            <>
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-2xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  Forgot Password
                </h2>
                <p className="text-[hsl(var(--muted-foreground))]">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.name@royaldornoch.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-password-email-input"
                      className="h-11 pl-10 bg-white border-[hsl(var(--border))] focus:border-[var(--rd-green)] focus:ring-[var(--rd-green)]"
                    />
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white font-medium"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>

                {/* Contact Admin Link */}
                <div className="mt-6 pt-6 border-t border-[hsl(var(--border))]">
                  <div className="flex items-start gap-3 text-sm text-[hsl(var(--muted-foreground))]">
                    <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="mb-1">Having trouble receiving the email?</p>
                      <a 
                        href={`mailto:${ADMIN_EMAIL}?subject=Password Reset Help - Royal Dornoch Training`}
                        className="text-[var(--rd-green)] hover:underline font-medium"
                        data-testid="contact-admin-link"
                      >
                        Contact Admin for assistance
                      </a>
                    </div>
                  </div>
                </div>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center">
              <div className="w-16 h-16 bg-[var(--rd-green)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[var(--rd-green)]" />
              </div>
              <h2 className="text-2xl text-[var(--rd-green)] mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
                Check Your Email
              </h2>
              <p className="text-[hsl(var(--muted-foreground))] mb-6">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
              <Button
                variant="outline"
                className="w-full h-11 border-[var(--rd-green)] text-[var(--rd-green)] hover:bg-[var(--rd-green)]/5 mb-4"
                onClick={() => setSubmitted(false)}
                data-testid="try-again-btn"
              >
                Try Another Email
              </Button>
              
              {/* Contact Admin in success state */}
              <div className="text-center">
                <a 
                  href={`mailto:${ADMIN_EMAIL}?subject=Password Reset Help - Royal Dornoch Training`}
                  className="text-sm text-[var(--rd-green)] hover:underline"
                  data-testid="contact-admin-success-link"
                >
                  Still having issues? Contact Admin
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
